/**
 * PATH: packages/email-hub/src/feature/lib/utils.ts
 * FILE_ID: EMAILHUB-FEATURE-LIB-UTILS-001
 * PURPOSE: Canonical body of this email lib module. Lifted
 *          from apps/<tenant>/src/features/email/lib/utils.ts
 *          (see docs/architecture/EMAIL_KANBAN_CUTOVER_ARC.md).
 *          Tenants import this via thin re-export shims; edit
 *          THIS file, not the tenant copies.
 * WHY:     Removes operator-visible drift between tenants —
 *          previously every email feature change had to be
 *          mirrored manually in two tenant trees, and concurrent
 *          agents kept letting one side go stale. One canonical
 *          body, both tenants consume from this package.
 * RELATED:
 *   - apps/<tenant>/src/features/email/lib/utils.ts: thin re-export
 *     shim that points back here.
 */

export function formatRelativeDate(iso: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  const now = new Date()
  const diff = now.getTime() - d.getTime()
  if (diff < 86400000 && d.getDate() === now.getDate()) {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }
  if (diff < 604800000) return d.toLocaleDateString([], { weekday: 'short' })
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' })
}

export function formatFullDate(iso: string): string {
  if (!iso) return ''
  return new Date(iso).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function getInitials(name: string): string {
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

export const CATEGORIES: Record<string, { label: string; color: string }> = {
  quote_request: { label: 'Quote Request', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
  quote_submission: { label: 'Quote Submission', color: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300' },
  purchase_order: { label: 'Purchase Order', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' },
  invoice: { label: 'Invoice', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
  payment: { label: 'Payment', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' },
  contract: { label: 'Contract', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' },
  variation: { label: 'Variation', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' },
  site_update: { label: 'Site Update', color: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300' },
  safety: { label: 'Safety', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
  delivery: { label: 'Delivery', color: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300' },
  supplier: { label: 'Supplier', color: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300' },
  general: { label: 'General', color: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300' },
  uncategorized: { label: 'Uncategorized', color: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400' },
}

export function getCategoryDisplay(category: string) {
  return CATEGORIES[category] || CATEGORIES.uncategorized
}

export const TAG_PRESET_COLORS = [
  '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6',
  '#EC4899', '#06B6D4', '#F97316', '#6366F1', '#14B8A6',
]

/**
 * Inline attachments (HTML body images referenced via `<img src="cid:...">`,
 * typically corporate signature logos or pasted screenshots) are rendered
 * BY the body sanitiser using the `inline_url` signed endpoint. They must
 * NOT also appear in the chip grid above the body or in the preview-modal
 * carousel — a long reply chain carries every previous signature forward,
 * snowballing into 30+ near-duplicate "image001.png" thumbnails.
 *
 * Mirrors the Gmail / Outlook web rule: any attachment with `is_inline`
 * truthy is suppressed from user-facing surfaces. Real user-attached
 * files leave `is_inline` falsy on the wire (Microsoft Graph + Gmail
 * API both honour this) and continue to render normally.
 *
 * Single source of truth — both `AttachmentList` (chip grid) and
 * `EmailReader` (preview-modal source) MUST run their attachments
 * through this same predicate so the chip's `onPreview(idx)` callback
 * stays index-aligned with the modal's carousel.
 */
export function isUserVisibleAttachment(att: { is_inline?: boolean }): boolean {
  return att.is_inline !== true
}

/**
 * Canonical "what the operator sees" attachment projection.
 *
 * Input:  the RAW attachment array straight off the email payload
 *         (may contain inline `cid:` signature/body images).
 * Output: a NEW array with inline attachments dropped and the
 *         survivors sorted smallest-first (stable tie-break on
 *         filename) — the smallest file's thumbnail decodes fastest,
 *         so the chip grid paints with an "instant" feel.
 *
 * WHY this exists as a shared helper: the chip grid (`AttachmentList`)
 * filters internally, but the preview-modal carousel is addressed by
 * INDEX. If a parent hands the chips a filtered view but the modal the
 * raw array, the index the chip emits points at a DIFFERENT attachment
 * in the modal — clicking photo #1 opens the inline signature logo
 * instead (BUG: "opened the wrong image"). Every surface that mounts
 * both `AttachmentList` and `AttachmentPreviewModal` (EmailReader,
 * EmailPreviewPane, EmailJobContextRail) MUST derive ONE list from this
 * helper and pass the SAME reference to both, so `onPreview(idx)` and
 * the carousel can never disagree. Keeping the projection here (not
 * inlined per-component) is the anti-drift guarantee.
 */
export function selectVisibleAttachments<
  T extends {
    is_inline?: boolean
    size?: number | null
    filename?: string | null
    mime_type?: string | null
  },
>(attachments: T[] | null | undefined): T[] {
  const filtered = (attachments ?? []).filter(isUserVisibleAttachment)
  // Collapse exact-duplicate attachments — same filename + byte size
  // (+ mime). Mail clients / the sync pipeline sometimes store the SAME
  // file multiple times (operator 2026-06-10: a Barrenjoey tax-invoice PDF
  // attached 7× showed as "7 attachments · 753 KB"). Each copy is pure
  // noise. Guard: only collapse when the byte size is KNOWN (>0) so
  // not-yet-hydrated rows (size 0/undefined) are never wrongly merged —
  // two distinct real files virtually never share filename AND exact size.
  // Done HERE (the single canonical projection) so the chip grid, the
  // thread bottom bar, and the preview-modal carousel all dedupe in
  // lock-step and ``onPreview(idx)`` stays index-aligned.
  const seen = new Set<string>()
  const deduped: T[] = []
  for (const att of filtered) {
    const size = att.size ?? 0
    if (size > 0) {
      const key = `${(att.filename ?? '').toLowerCase()}\u0000${size}\u0000${att.mime_type ?? ''}`
      if (seen.has(key)) continue
      seen.add(key)
    }
    deduped.push(att)
  }
  return deduped.sort((a, b) => {
    const bySize = (a.size ?? 0) - (b.size ?? 0)
    if (bySize !== 0) return bySize
    return (a.filename ?? '').localeCompare(b.filename ?? '')
  })
}
